import type { ClientListSortDir, ClientViewFilter } from "@repo/shared/types";
import { useMemo } from "react";
import { useSearchParams } from "react-router";

/**
 * QL-349 — filters, sort, view and page live in the URL, so a bookmarked or
 * shared `/clients` link reproduces the same list. A deliberate deviation
 * from this codebase's usual "plain React state" filter-bar convention (see
 * `/compliance/flows-review`): the ticket requires it, not just this page.
 *
 * Changing anything except the page itself returns to page 1 (§3 of the
 * ticket) — modelled here by DELETING the `page` param rather than writing
 * "1", so a freshly-filtered URL doesn't carry a redundant `page=1`.
 *
 * Search is intentionally NOT synced on every keystroke — the caller debounces
 * it locally and calls `setSearch` only with the settled value, the same
 * split flows-review and the old client-side `/clients` state used.
 */

export type ClientListUrlState = {
  page: number;
  sortBy: string;
  sortDir: ClientListSortDir;
  viewId: string | undefined;
  search: string;
  filters: ClientViewFilter[];
  setPage: (page: number) => void;
  setSort: (sortBy: string, sortDir: ClientListSortDir) => void;
  setViewId: (viewId: string) => void;
  setSearch: (search: string) => void;
  setFilters: (filters: ClientViewFilter[]) => void;
};

function parseFilters(raw: string | null): ClientViewFilter[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    // Elements are only checked for being filter-shaped here (the registry and
    // the server do the real validation) — enough that a stray `null` in a
    // hand-edited URL can't crash the bar.
    return Array.isArray(parsed)
      ? parsed.filter((f) => typeof f === "object" && f !== null && typeof f.type === "string")
      : [];
  } catch {
    return [];
  }
}

export function useClientListUrlState(defaultSortBy: string): ClientListUrlState {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, Number(searchParams.get("page")) || 1);
  const sortBy = searchParams.get("sortBy") || defaultSortBy;
  const sortDir: ClientListSortDir = searchParams.get("sortDir") === "desc" ? "desc" : "asc";
  const viewId = searchParams.get("viewId") ?? undefined;
  const search = searchParams.get("search") || "";
  const filters = useMemo(() => parseFilters(searchParams.get("filters")), [searchParams]);

  const update = (patch: Record<string, string | null>, resetPage: boolean) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(patch)) {
          if (value === null || value === "") next.delete(key);
          else next.set(key, value);
        }
        if (resetPage) next.delete("page");
        return next;
      },
      { replace: true },
    );
  };

  return {
    page,
    sortBy,
    sortDir,
    viewId,
    search,
    filters,
    setPage: (p) => update({ page: p > 1 ? String(p) : null }, false),
    setSort: (by, dir) => update({ sortBy: by, sortDir: dir }, true),
    setViewId: (id) => update({ viewId: id }, true),
    setSearch: (s) => update({ search: s || null }, true),
    setFilters: (f) => update({ filters: f.length > 0 ? JSON.stringify(f) : null }, true),
  };
}
